import { Box } from '@nimbus-ds/components';
import OrderInvoiceResponseDto from '@tiendanube/common/src/domains/orders/commons/OrderInvoiceResponseDto';
import InvoiceOperationEnum from '@tiendanube/common/src/enums/InvoiceOperation';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import ExternalInvoiceSection from '../ExternalInvoiceSection';
import InvoiceSection from '../InvoiceSection';
import OptionInvoicePopover from '../OptionInvoicePopover';

interface InvoicesProps {
  readonly invoices: OrderInvoiceResponseDto[];
  readonly isExternalInvoicesEnabled: boolean;
  readonly t: ReturnType<typeof useTranslationOrders>;
  onOpenInvoiceOperationModal: (
    operation: InvoiceOperationEnum,
    number: string,
    reason?: string,
  ) => void;
}

function Invoices({
  invoices,
  isExternalInvoicesEnabled,
  t,
  onOpenInvoiceOperationModal,
}: Readonly<InvoicesProps>) {
  if (!invoices?.length) return null;

  const hasErrors = (event?: { error_message?: string[] }) =>
    (event?.error_message?.length ?? 0) > 0;

  const sortedInvoices = [...invoices].sort((a, b) => b.id - a.id);

  return (
    <Box>
      {sortedInvoices.map((invoice, index) => {
        const {
          id,
          invoice_number,
          xml_url,
          pdf_url,
          file_url,
          last_correction_event,
          cancellation_event,
          is_external,
        } = invoice;

        const showExternalInvoice = is_external && isExternalInvoicesEnabled;
        const isCancelledSuccessfully =
          !!cancellation_event &&
          !hasErrors(cancellation_event) &&
          !showExternalInvoice;
        const showCorrection =
          last_correction_event?.event_type ===
            InvoiceOperationEnum.CORRECTION && !showExternalInvoice;
        const showCancellation =
          cancellation_event?.event_type ===
            InvoiceOperationEnum.CANCELLATION && !showExternalInvoice;
        const hasAnyExternalInvoice = sortedInvoices.some(
          (invoice) => invoice.is_external,
        );

        return (
          <Box
            key={id}
            paddingY="2"
            borderTopWidth={index > 0 ? '1' : undefined}
            borderStyle="solid"
            borderColor="neutral-surfaceDisabled"
            paddingTop={invoices.length === 1 ? 'none' : '2'}
          >
            {showExternalInvoice && (
              <ExternalInvoiceSection
                title={t('cardInvoice.externalInvoice.successGeneric')}
                fileUrl={file_url}
                t={t}
              />
            )}

            {!showExternalInvoice && !hasAnyExternalInvoice && (
              <InvoiceSection
                title={t('cardInvoice.invoice.succes', { key: invoice_number })}
                xmlUrl={xml_url}
                pdfUrl={isCancelledSuccessfully ? undefined : pdf_url}
                t={t}
              />
            )}

            {showCorrection && !hasAnyExternalInvoice && (
              <InvoiceSection
                title={t('cardInvoice.invoice.edit')}
                errorTitle={t('cardInvoice.alert.edit.title')}
                xmlUrl={
                  hasErrors(last_correction_event)
                    ? undefined
                    : last_correction_event?.xml_url
                }
                pdfUrl={
                  hasErrors(last_correction_event)
                    ? undefined
                    : last_correction_event?.pdf_url
                }
                errorMessage={last_correction_event?.error_message}
                t={t}
              />
            )}

            {showCancellation && !hasAnyExternalInvoice && (
              <InvoiceSection
                title={t('cardInvoice.invoice.cancel')}
                errorTitle={t('cardInvoice.alert.cancel.title')}
                xmlUrl={
                  hasErrors(cancellation_event)
                    ? undefined
                    : cancellation_event?.xml_url
                }
                pdfUrl={
                  hasErrors(cancellation_event)
                    ? undefined
                    : cancellation_event?.pdf_url
                }
                errorMessage={cancellation_event?.error_message}
                t={t}
              />
            )}

            {!isCancelledSuccessfully &&
              !showExternalInvoice &&
              !hasAnyExternalInvoice && (
                <OptionInvoicePopover
                  onOpenModal={onOpenInvoiceOperationModal}
                  invoice={invoice}
                  t={t}
                />
              )}
          </Box>
        );
      })}
    </Box>
  );
}

export default Invoices;
